import { addDays, addHours, startOfToday, differenceInDays } from 'date-fns'

export interface Speaker {
  name: string
  linkedin?: string
  title?: string
}

export interface AgendaItem {
  time: string
  title: string
  description?: string
  speakers?: Speaker[]
}

export interface EventType {
  id: string
  slug: string
  title: string
  description: string
  longDescription?: string
  type: 'demo' | 'webinar' | 'workshop' | 'tour' | 'launch'
  category: string
  locationType?: 'online' | 'on-site' | 'hybrid'
  startTime: Date
  endTime: Date
  location: string
  presenter: string
  maxAttendees: number
  currentAttendees: number
  imageUrl?: string
  tags: string[]
  isLive?: boolean
  isFeatured?: boolean
  registrationUrl?: string
  meetingUrl?: string
  requirements?: string[]
  agenda?: string[]
  agendaItems?: AgendaItem[]
  relatedDemoSlugs?: string[]
  gradientFrom: string
  gradientTo: string
  glowColor: string
}

const today = startOfToday()

// Calculate days until November 19, 2025
const targetDate = new Date('2025-11-19')
const daysUntilHPEEvent = differenceInDays(targetDate, today)

export const mockEvents: EventType[] = [
  {
    id: '1',
    slug: 'hpe-aruba-cx-10000-series-live-demo',
    title: 'HPE Aruba CX 10000 Series Live Demo',
    description: 'Experience the power of next-generation switching with our flagship CX 10000 series. See advanced automation, AI-driven insights, and zero-touch provisioning in action.',
    type: 'demo',
    category: 'Switching Solutions',
    startTime: addHours(addDays(today, 2), 14),
    endTime: addHours(addDays(today, 2), 15.5),
    location: 'Virtual Demo Lab',
    presenter: 'Sarah Mitchell - Senior Network Architect',
    maxAttendees: 25,
    currentAttendees: 18,
    imageUrl: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800&q=80',
    tags: ['Switching', 'AI', 'Automation', 'Enterprise'],
    isFeatured: true,
    registrationUrl: '/register/cx-demo',
    requirements: ['Basic networking knowledge', 'Web browser with camera/mic'],
    agenda: [
      'CX 10000 Hardware Overview',
      'AOS-CX Operating System Deep Dive',
      'AI-Powered Network Analytics',
      'Zero-Touch Provisioning Demo',
      'Q&A Session'
    ],
    gradientFrom: '#3B82F6',
    gradientTo: '#8B5CF6',
    glowColor: '#60A5FA'
  },
  {
    id: '2',
    slug: 'wifi-7-performance-showcase',
    title: 'Wi-Fi 7 Performance Showcase',
    description: 'Witness the future of wireless connectivity with Wi-Fi 7 technology. Multi-link operation, 6GHz spectrum utilization, and unprecedented throughput.',
    type: 'demo',
    category: 'Wireless Solutions',
    startTime: addHours(addDays(today, 5), 10),
    endTime: addHours(addDays(today, 5), 11),
    location: 'Demo Lab - Building A',
    presenter: 'Marcus Chen - Wireless Technology Lead',
    maxAttendees: 15,
    currentAttendees: 12,
    imageUrl: 'https://images.unsplash.com/photo-1544197150-b99a580bb7a8?w=800&q=80',
    tags: ['Wi-Fi 7', 'Wireless', '6GHz', 'Performance'],
    registrationUrl: '/register/wifi7-demo',
    requirements: ['Wi-Fi 7 capable device recommended', 'On-site attendance'],
    agenda: [
      'Wi-Fi 7 Technology Overview',
      'Multi-Link Operation Demo',
      '6GHz Spectrum Benefits',
      'Real-world Performance Testing',
      'Comparison with Wi-Fi 6/6E'
    ],
    gradientFrom: '#10B981',
    gradientTo: '#06B6D4',
    glowColor: '#34D399'
  },
  {
    id: '3',
    slug: 'zero-trust-architecture-network-security',
    title: 'Zero Trust Architecture: The Future of Network Security',
    description: 'Join our security experts for an in-depth discussion on implementing Zero Trust principles in modern enterprise networks.',
    type: 'webinar',
    category: 'Security',
    locationType: 'online',
    startTime: addHours(addDays(today, 7), 16),
    endTime: addHours(addDays(today, 7), 17),
    location: 'Online Webinar',
    presenter: 'Dr. Elena Rodriguez - Chief Security Officer',
    maxAttendees: 500,
    currentAttendees: 287,
    imageUrl: 'https://images.unsplash.com/photo-1591115765373-5207764f72e7?w=800&q=80',
    tags: ['Security', 'Zero Trust', 'Architecture', 'Best Practices'],
    isFeatured: true,
    meetingUrl: 'https://zoom.us/webinar/123',
    agenda: [
      'Zero Trust Fundamentals',
      'Implementation Strategies',
      'Common Pitfalls and Solutions',
      'Case Studies',
      'Live Q&A'
    ],
    gradientFrom: '#F59E0B',
    gradientTo: '#EF4444',
    glowColor: '#FBBF24'
  },
  {
    id: '4',
    slug: 'hpe-aruba-sse-peak-performance-workshop',
    title: 'HPE Aruba Networking - SSE Peak Performance Workshop',
    description: 'This workshop provides a full SSE environment, allowing customers and prospects to get hands-on experience with our leading SSE solution (formerly Axis Security).',
    longDescription: 'Participants can experiment and deploy a ZTNA/SWG/DLP solution in under an hour. A custom lab guide is generated for each individual lab environment, enabling users to progress at their own pace. Total seats available for these sessions is x15 candidates and maximum x2 people per Reseller. Please register early to avoid disappointment.',
    type: 'workshop',
    category: 'Security & SSE',
    locationType: 'on-site',
    startTime: new Date('2025-12-07T10:00:00'),
    endTime: new Date('2025-12-07T13:00:00'),
    location: 'HPE Office - 210 Wharfedale Rd, Winnersh, Wokingham RG41 5TP',
    presenter: 'Jack Tokarski, Graham Muncer, and Stelios',
    maxAttendees: 15,
    currentAttendees: 8,
    imageUrl: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&q=80',
    tags: ['SSE', 'ZTNA', 'SWG', 'DLP', 'Security', 'Hands-on'],
    isFeatured: true,
    registrationUrl: '/register/sse-workshop',
    relatedDemoSlugs: ['axis-redirect'],
    requirements: [
      'Laptop with web browser',
      'Interest in SSE/ZTNA solutions',
      'Maximum 2 people per Reseller'
    ],
    agendaItems: [
      {
        time: '10:00 - 10:30',
        title: 'Arrivals and Welcome',
        speakers: [
          { name: 'Stelios Papakyriacou 💍 Just Married', linkedin: 'https://www.linkedin.com/in/stelios-papakyriacou-551621a4/', title: 'HPE Aruba BDM' },
          { name: 'John Martin', linkedin: 'https://www.linkedin.com/in/john-martin-28921413b/', title: 'SASE System Engineer' }
        ]
      },
      {
        time: '10:30 - 11:00',
        title: 'Introduction to SSE and Challenges of Legacy VPN',
        speakers: [
          { name: 'Graham Muncer', linkedin: 'https://www.linkedin.com/in/graham-muncer/', title: 'Solutions Architect' }
        ]
      },
      {
        time: '11:00 - 11:30',
        title: 'ZTNA vs. VPN',
        speakers: [
          { name: 'Graham Muncer', linkedin: 'https://www.linkedin.com/in/graham-muncer/', title: 'Solutions Architect' }
        ]
      },
      {
        time: '11:30 - 12:00',
        title: 'HPE SSE Demo',
        description: 'Live demonstration of HPE Aruba SSE capabilities',
        speakers: [
          { name: 'Jack Tokarski', linkedin: 'https://www.linkedin.com/in/jacktokarski/', title: 'Head Solutions Architect' }
        ]
      },
      {
        time: '12:00 - 12:30',
        title: 'ZTNA, SWG, DLP Hands-on Lab',
        description: 'Participants experiment with custom lab environments at their own pace'
      },
      {
        time: '12:30 - 13:00',
        title: 'Use Cases, Next Steps & Q&A',
        description: 'Discussion of real-world use cases and open Q&A session',
        speakers: [
          { name: 'Jack Tokarski', linkedin: 'https://www.linkedin.com/in/jacktokarski/', title: 'Head Solutions Architect' },
          { name: 'Graham Muncer', linkedin: 'https://www.linkedin.com/in/graham-muncer/', title: 'Solutions Architect' }
        ]
      }
    ],
    gradientFrom: '#059669',
    gradientTo: '#7C3AED',
    glowColor: '#10B981'
  },
  {
    id: '6',
    slug: 'hpe-aruba-next-gen-edge-platform-launch',
    title: 'HPE Aruba Next-Gen Edge Platform Launch',
    description: 'World premiere of our revolutionary edge computing platform. Be the first to see the future of distributed computing.',
    type: 'launch',
    category: 'Product Launch',
    startTime: new Date('2025-10-10T15:00:00'),
    endTime: new Date('2025-10-10T16:30:00'),
    location: 'Auditorium - Main Campus',
    presenter: 'CEO - Michael Foster',
    maxAttendees: 200,
    currentAttendees: 145,
    imageUrl: 'https://images.unsplash.com/photo-1505373877841-8d25f7d46678?w=800&q=80',
    tags: ['Launch', 'Edge Computing', 'Innovation', 'Platform'],
    registrationUrl: '/register/edge-launch',
    agenda: [
      'Welcome & Vision',
      'Platform Architecture Deep Dive',
      'Live Technology Demo',
      'Customer Success Stories',
      'Roadmap & Availability'
    ],
    gradientFrom: '#7C3AED',
    gradientTo: '#DB2777',
    glowColor: '#A855F7'
  },
  {
    id: '7',
    slug: 'hpe-networking-cic-event',
    title: 'HPE Networking - CIC Event',
    description: 'Exclusive partner enablement event at the HPE Customer Innovation Centre in London. Discover how Westcoast, as the only UK distributor for both HPE Aruba & HPE Juniper, can help you grow your networking business. Experience live demos, strategy sessions, and networking opportunities.',
    longDescription: 'Join us for an immersive partner event at HPE\'s prestigious Customer Innovation Centre in the heart of London. Gain exclusive insights into HPE Aruba and Juniper technologies, learn winning strategies from industry experts, and explore new revenue opportunities. Network with fellow partners while experiencing cutting-edge demonstrations of AI-driven networking solutions that are transforming enterprise infrastructure.',
    type: 'workshop',
    category: 'Partner Enablement',
    locationType: 'on-site',
    startTime: addHours(addDays(today, daysUntilHPEEvent), 12.5),
    endTime: addHours(addDays(today, daysUntilHPEEvent), 17.25),
    location: 'HPE CIC, 1 Aldermanbury, London EC2V 7HR',
    presenter: 'Phil Bell, Simon Wilson, and Industry Experts',
    maxAttendees: 75,
    currentAttendees: 42,
    imageUrl: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&q=80',
    tags: ['HPE', 'Aruba', 'Juniper', 'Partner Program', 'CIC', 'Networking Strategy', 'AI-driven Networking'],
    isFeatured: true,
    registrationUrl: '/register/hpe-cic-event',
    relatedDemoSlugs: ['aruba-central', 'clearpass-nac', 'axis-redirect', 'uxi-demo'],
    requirements: [
      'Partner status with Westcoast UK',
      'Interest in HPE Aruba and/or Juniper solutions',
      'On-site attendance in London'
    ],
    agendaItems: [
      {
        time: '12:30 - 13:00',
        title: 'Arrivals and Welcome',
        description: 'Registration and light refreshments'
      },
      {
        time: '13:00 - 13:15',
        title: 'Welcome and Introductions',
        description: 'Overview of Westcoast\'s role as the only UK distributor holding contracts with HPE Aruba & HPE Juniper. Setting the scene: why this matters for partners.',
        speakers: [
          { name: 'Phil Bell', linkedin: 'https://www.linkedin.com/in/phil-bell-57547018/', title: 'Chief Revenue Officer' }
        ]
      },
      {
        time: '13:15 - 13:30',
        title: 'Introduction to the Customer Innovation Centre',
        speakers: [
          { name: 'Nyla Kongsinh', linkedin: 'https://www.linkedin.com/in/nkongsinh/', title: 'Sr. Technology Evangelist' }
        ]
      },
      {
        time: '13:30 - 14:15',
        title: 'HPE Networking Strategy',
        description: 'How Aruba Networking and Juniper AI-driven networking complement each other. Market outlook: growth opportunities for partners.',
        speakers: [
          { name: 'Simon Wilson', linkedin: 'https://www.linkedin.com/in/simon-wilson-5a180b1/', title: 'HPE Aruba CTO' }
        ]
      },
      {
        time: '14:15 - 14:45',
        title: 'HPE Vantage Partner Program',
        speakers: [
          { name: 'Pedro Cunha', linkedin: 'https://www.linkedin.com/in/pcunha14/', title: 'Distribution Business Lead' },
          { name: 'Anna Chalmers', linkedin: 'https://www.linkedin.com/in/anna-chalmers-0a6b85/', title: 'UK&I Channel Leader' }
        ]
      },
      {
        time: '14:45 - 15:00',
        title: 'Break',
        description: 'Coffee and networking'
      },
      {
        time: '15:00 - 15:20',
        title: 'Partner Enablement: Growing with Westcoast UK',
        description: 'Transition support for HPE/Juniper processes. Westcoast value-add services (training, labs, marketing support, technical expertise).',
        speakers: [
          { name: 'Troy Carpenter', linkedin: 'https://www.linkedin.com/in/troy-carpenter-b903573/', title: 'HPE Juniper BDM' },
          { name: 'Stelios Papakyriacou 💍 Just Married', linkedin: 'https://www.linkedin.com/in/stelios-papakyriacou-551621a4/', title: 'HPE Aruba BDM' }
        ]
      },
      {
        time: '15:20 - 15:40',
        title: 'How to Approach Customer Conversations',
        speakers: [
          { name: 'Grace Lewis', linkedin: 'https://www.linkedin.com/in/grace-lewis-066482161/', title: 'HPE Aruba EU AM' }
        ]
      },
      {
        time: '15:40 - 16:00',
        title: 'Panel Discussion: "Bringing It All Together"',
        description: 'How partners can position Aruba for the campus/edge and Juniper for the data centre/AI networking. Open Q&A session.',
        speakers: [
          { name: 'Panel', title: 'Westcoast UK + HPE Aruba + HPE Juniper + Partner Guest' }
        ]
      },
      {
        time: '16:00 - 16:15',
        title: 'Wrap-up and Next Steps',
        speakers: [
          { name: 'Anna Chalmers', linkedin: 'https://www.linkedin.com/in/anna-chalmers-0a6b85/', title: 'UK&I Channel Leader' }
        ]
      },
      {
        time: '16:15 - 17:15',
        title: 'Demo & Networking Session',
        description: 'Central, Mist, and Apstra live demonstrations followed by drinks in local bar',
        speakers: [
          { name: 'Jack Tokarski', linkedin: 'https://www.linkedin.com/in/jacktokarski/', title: 'Head Solutions Architect' },
          { name: 'Graham Muncer', linkedin: 'https://www.linkedin.com/in/graham-muncer/', title: 'Solutions Architect' }
        ]
      }
    ],
    gradientFrom: '#01A982',
    gradientTo: '#0096D6',
    glowColor: '#00C8B4'
  },
  {
    id: '8',
    slug: 'win-with-westcoast-2025',
    title: 'Win with Westcoast 2025',
    description: 'See inside the future and step in with Westcoast. Join us at the prestigious Nobu Hotel London for an exclusive partner event featuring industry insights, breakout sessions, and networking opportunities.',
    longDescription: 'Win with Westcoast 2025 is our flagship partner event bringing together senior decision makers and business influencers for a day of innovation, collaboration, and strategic networking. Experience thought-provoking sessions covering AI, cloud solutions, sustainability, and evolving threat landscapes, followed by an evening of entertainment and networking at one of London\'s most iconic venues.',
    type: 'workshop',
    category: 'Partner Event',
    locationType: 'on-site',
    startTime: new Date('2025-11-05T10:00:00'),
    endTime: new Date('2025-11-05T19:30:00'),
    location: 'Nobu Hotel - 22 Portman Square, London W1H 7BG',
    presenter: 'Phil Bell, Steve Brazier, and Industry Experts',
    maxAttendees: 150,
    currentAttendees: 0,
    imageUrl: '/images/branded/1.png',
    tags: ['Partner Event', 'AI', 'Cloud', 'Sustainability', 'Networking', 'Strategy', 'Innovation'],
    isFeatured: true,
    registrationUrl: '/register/win-with-westcoast-2025',
    requirements: [
      'Senior decision makers and business influencers',
      'Partner status with Westcoast UK',
      'On-site attendance in London'
    ],
    agendaItems: [
      {
        time: '10:00 - 10:30',
        title: 'Welcome Drinks',
        description: 'Registration and networking over refreshments',
        speakers: []
      },
      {
        time: '10:30 - 11:30',
        title: 'Welcome from Phil Bell Westcoast on Channel Trends',
        description: 'Opening keynote on current channel market trends and opportunities',
        speakers: [
          { name: 'Phil Bell', linkedin: 'https://www.linkedin.com/in/phil-bell-57547018/', title: 'Chief Revenue Officer, Westcoast' }
        ]
      },
      {
        time: '11:30 - 11:50',
        title: 'Break',
        description: 'Coffee break and networking'
      },
      {
        time: '11:50 - 12:30',
        title: 'Workplace Panel',
        description: 'Discussion on the evolving workplace and technology requirements'
      },
      {
        time: '12:30 - 12:40',
        title: 'Spotlight on Sustainability',
        description: 'Brief session on sustainable technology practices'
      },
      {
        time: '12:40 - 13:10',
        title: 'Steve Brazier – Channel Market Overview',
        description: 'Comprehensive market analysis and trends',
        speakers: [
          { name: 'Steve Brazier', title: 'Industry Analyst' }
        ]
      },
      {
        time: '13:10 - 14:10',
        title: 'Lunch',
        description: 'Networking lunch in the Mezzanine'
      },
      {
        time: '14:10 - 14:30',
        title: 'Breakout: AI in Action',
        description: 'Practical AI applications for business'
      },
      {
        time: '14:10 - 14:30',
        title: 'Breakout: Value of Services',
        description: 'Monetizing services in the modern channel'
      },
      {
        time: '14:30 - 14:50',
        title: 'Breakout: Evolving Threat Landscape',
        description: 'Understanding and addressing modern security threats'
      },
      {
        time: '14:30 - 14:50',
        title: 'Breakout: AI Consultancy Services',
        description: 'Building AI consulting practices'
      },
      {
        time: '14:50 - 15:10',
        title: 'Breakout: Monetizing the Lifecycle of Services',
        description: 'Creating recurring revenue through managed services'
      },
      {
        time: '14:50 - 15:10',
        title: 'Breakout: Building resilience',
        description: 'Business continuity and resilience strategies'
      },
      {
        time: '15:10 - 15:30',
        title: 'Breakout: Demystifying AI',
        description: 'Making AI accessible and understandable'
      },
      {
        time: '15:10 - 15:30',
        title: 'Breakout: Mobile Solutions',
        description: 'Modern mobile device management and solutions'
      },
      {
        time: '15:30 - 15:50',
        title: 'Breakout: Cloud Partner Thrive',
        description: 'Success strategies for cloud partners'
      },
      {
        time: '15:30 - 15:50',
        title: 'Breakout: Monetising AI',
        description: 'Revenue opportunities in AI solutions'
      },
      {
        time: '15:50 - 16:10',
        title: 'Break',
        description: 'Afternoon refreshments in the Mezzanine'
      },
      {
        time: '16:10 - 16:40',
        title: 'Breakout: Adapting to a service driven market panel with Steve Brazier',
        description: 'Panel discussion on service transformation'
      },
      {
        time: '16:10 - 16:40',
        title: 'Breakout: Apple',
        description: 'Apple ecosystem and business opportunities'
      },
      {
        time: '16:40 - 17:20',
        title: 'Tech Solutions Panel',
        description: 'Technology solution providers discuss market trends'
      },
      {
        time: '17:20 - 17:30',
        title: 'Phil Bell Closing',
        description: 'Closing remarks and next steps',
        speakers: [
          { name: 'Phil Bell', linkedin: 'https://www.linkedin.com/in/phil-bell-57547018/', title: 'Chief Revenue Officer, Westcoast' }
        ]
      },
      {
        time: '17:30 - 19:30+',
        title: 'Dinner, drinks, entertainment, networking',
        description: 'Evening reception with dinner, drinks, and networking in the Mezzanine'
      }
    ],
    gradientFrom: '#BBD134',
    gradientTo: '#059669',
    glowColor: '#BBD134'
  }
]

// Helper functions
export const getEventsByType = (type: EventType['type']) =>
  mockEvents.filter(event => event.type === type)

export const getFeaturedEvents = () => {
  const now = new Date()
  return mockEvents.filter(event => event.isFeatured && event.endTime > now)
}

export const getUpcomingEvents = (limit?: number) => {
  const upcoming = mockEvents
    .filter(event => event.startTime > new Date())
    .sort((a, b) => a.startTime.getTime() - b.startTime.getTime())

  return limit ? upcoming.slice(0, limit) : upcoming
}

export const getEventById = (id: string) =>
  mockEvents.find(event => event.id === id)

export const getEventBySlug = (slug: string) =>
  mockEvents.find(event => event.slug === slug)